/*
 * Module for KnowledgeBase menu above the menu bar
 * Manages Kb specific information, such as the contexts, languages, touchpoints, and statuses
 */

define([
  "marionette",
  "messaging/messenger",
  "modules/header/kbMenu/kbMenuView",
  "modules/header/kbMenu/kbMenuModel",
  "modules/authoringAdmin/campaigns/campaign/campaignModel",
], function (Marionette, Messenger, KbMenuView, KbMenuModel, CampaignModel) {
  var KbMenuController = Marionette.Controller.extend({
    initialize: function (options) {
      this.name = "kbMenu";
      this.region = options.region;
      //kb statuses, contexts, and touchpoints
      this.statuses = null;
      this.contexts = null;
      this.touchpoints = null;
      this.languages = null;
      this.languagesWithDefault = null;
      this.campaigns = null;
      this.campaignsWithDefault = null;
      this.categories = null;
      this.attributes = null;
      this.attributeTypes = null;
      this.stopWords = null;
      this.martStatuses = null;
      this.privilegedAreaRoles = null;
      this.contentAwarenessIgnoredUrls = null;
      this._getKbs(); // This must be done in initialize, not show, otherwise timing issues can make other things fail
      this._setUpCommands();
      this._setUpReqres();
    },
    show: function () {
      this.view = new KbMenuView.kbMenuView({
        model: new KbMenuModel.kbModel(),
        collection: this.kbs,
      });
      //changed KB from menu
      this.view.on(
        "itemview:click:kb",
        function (clickedView) {
          //only change KB is a new KB was selected
          if (this.region.currentView.model !== clickedView.model) {
            //save, if needed
            Messenger.save(function (success) {
              if (success) {
                //need to display warning message asking user if they are sure they want to switch KBs
                this.setKb(clickedView.model);
                this._loadKbDetails();
                //trigger event that kb changed
                console.log("active kb changed to " + this.activeKbId);

                Messenger.vent.trigger("kb:changed");
              } else {
                //error, so close dropdown
                this._closeDropdown();
              }
            }, this);
          } else {
            //close dropdown
            this._closeDropdown();
          }
        },
        this,
      );
      this.showRegion(this.view);
    },
    setKb: function (model) {
      var kbId = model.get("kbId");
      var tenantId = model.get("tenantId");
      if (!kbId) {
        throw new Error("No KB ID given");
      }
      if (!tenantId) {
        throw new Error("No Tenant ID given");
      }
      this.region.currentView.model = model;
      //set active kb Id
      this.activeKbId = kbId;
      this.activeTenantId = tenantId;
      this.activeTenant = Messenger.reqres.request("fetch:tenant", tenantId);
      if (Messenger.reqres.request("user:is:author") || Messenger.reqres.request("user:is:admin")) {
        Messenger.vent.trigger("get:refresh:status"); //tells the kb refresh menu that it's time to load
      }
      this.region.currentView.render();
      const contentDownloadUrl = Messenger.reqres.request("get:download:url");
      this.view.setResponseStylesLink(`${contentDownloadUrl}${kbId}/responseStyles.css`);
      Messenger.command.execute("update:menu:bar");
    },
    _closeDropdown: function () {
      this.view.toggleDropdown();
    },
    _getKbs: function () {
      //set active KB after KBs load
      this.kbs = Messenger.reqres.request("kbs");
      this.kbs.on(
        "sync",
        function () {
          // Might not be an active KB if the Admin area is being shown initially
          if (this.activeKbId) {
            var kb = this.kbs.get(this.activeKbId);
            if (!kb) {
              throw new Error("Invalid KB " + this.activeKbId);
            }
            console.log("_set kb " + this.activeKbId);
            this.setKb(kb);
          }
        },
        this,
      );
    },
    _loadKbDetails: function () {
      //load statuses, contexts,touchpoints, and languages
      this.statuses = new KbMenuModel.statuses({ kbId: this.activeKbId });
      this.contexts = new KbMenuModel.contexts({ kbId: this.activeKbId });
      this.touchpoints = new KbMenuModel.touchpoints({ kbId: this.activeKbId });
      this.languages = new KbMenuModel.languages({ kbId: this.activeKbId });
      this.languagesWithDefault = new KbMenuModel.languagesWithDefault({ kbId: this.activeKbId });
      this.campaigns = new CampaignModel.campaigns({ kbId: this.activeKbId });
      this.campaignsWithDefault = new CampaignModel.campaignsWithDefault({ kbId: this.activeKbId });
      this.categories = new KbMenuModel.categories({ kbId: this.activeKbId });
      if (
        Messenger.reqres.request("user:is:author") ||
        Messenger.reqres.request("user:is:admin") ||
        Messenger.reqres.request("user:is:reporter")
      ) {
        this.agents = new KbMenuModel.agents({ kbId: this.activeKbId });
        this.martStatuses = new KbMenuModel.martStatuses({ kbId: this.activeKbId });
      }
      if (
        Messenger.reqres.request("user:is:author") ||
        Messenger.reqres.request("user:is:admin") ||
        Messenger.reqres.request("user:is:reviewer") ||
        Messenger.reqres.request("user:is:reporter")
      ) {
        this.privilegedAreaRoles = new KbMenuModel.privilegedAreaRoles({ kbId: this.activeKbId });
      }
      if (Messenger.reqres.request("user:is:author" || Messenger.reqres.request("user:is:admin"))) {
        this.contentAwarenessIgnoredUrls = new KbMenuModel.contentAwarenessIgnoredUrls({
          kbId: this.activeKbId,
        });
      }
      this.defaultUrl = new KbMenuModel.defaultUrl({ kbId: this.activeKbId });
      //only load the stop words once since they are KB independent
      if (this.stopWords === null) {
        //this.stopWords = new KbMenuModel.stopWords();
      }
      this.attributes = new KbMenuModel.attributes({ kbId: this.activeKbId });
      this.attributeTypes = new KbMenuModel.attributeTypes({ kbId: this.activeKbId });
    },
    _setKbById: function (kbId) {
      if (typeof kbId !== "undefined") {
        if (kbId != this.activeKbId) {
          //intentionally != rather than !==.
          //find matching KB with the provided KbId
          var kb = this.kbs.get(kbId);
          if (typeof kb !== "undefined") {
            console.log("_set kb " + kbId);
            this.setKb(kb);
          } else {
            this.activeKbId = kbId; //save active kbId for after kbs load
          }
          this._loadKbDetails();
        }
      }
    },
    _setUpReqres: function () {
      Messenger.reqres.setHandler(
        "active:kb",
        function () {
          return this.activeKbId || Messenger.reqres.request("default:kbId");
        },
        this,
      );
      //get all kbs (that the user can see...)
      Messenger.reqres.setHandler(
        "kb:list",
        function () {
          return this.kbs;
        },
        this,
      );
      //get secret keys of all kbs
      Messenger.reqres.setHandler(
        "kb:secretKeys:list",
        function () {
          if (!this.kbSecretKeys) {
            this.kbSecretKeys = Messenger.reqres.request("kbSecretKeys");
          }
          return this.kbSecretKeys;
        },
        this,
      );
      Messenger.reqres.setHandler(
        "kb:by:name",
        function (name) {
          for (var i = 0; i < this.kbs.models.length; i++) {
            var kb = this.kbs.models[i];
            if (kb.get("kbName") === name) {
              return kb;
            }
          }
        },
        this,
      );
      //get all statuses
      Messenger.reqres.setHandler(
        "kb:statuses",
        function () {
          return this.statuses;
        },
        this,
      );
      //get only publishable statuses. i.e publish, enable and disable
      Messenger.reqres.setHandler(
        "kb:publishable:topic:statuses",
        function () {
          const publishableStatusIds = [
            KbMenuModel.statuses.NON_VERSIONED_ENABLED,
            KbMenuModel.statuses.NON_VERSIONED_DISABLED,
            KbMenuModel.statuses.VERSIONED_PUBLISHED,
            KbMenuModel.statuses.VERSIONED_DISABLED,
          ];
          return this.statuses
            .get("allTopicStatuses")
            .filter((status) => publishableStatusIds.includes(status.statusId));
        },
        this,
      );
      //get all contexts
      Messenger.reqres.setHandler(
        "kb:contexts",
        function () {
          return this.contexts;
        },
        this,
      );
      //get all touchpoints
      Messenger.reqres.setHandler(
        "kb:touchpoints",
        function () {
          return this.touchpoints;
        },
        this,
      );
      //get all languages enabled for this KB
      Messenger.reqres.setHandler(
        "kb:languages",
        function () {
          return this.languages;
        },
        this,
      );
      //get all languages enabled for this KB plus a "default" language
      Messenger.reqres.setHandler(
        "kb:languagesWithDefault",
        function () {
          return this.languagesWithDefault;
        },
        this,
      );
      // Checks whether a particular language is English
      Messenger.reqres.setHandler(
        "kb:language:is:english",
        function (language) {
          var languageModel;
          if (typeof language == "number") {
            var languageId = language;
            languageModel = this.languages.get(languageId);
          } else if (typeof language == "object") {
            languageModel = language;
          } else if (typeof language == "string") {
            languageModel = this.languages.findWhere({ locale: language });
          }
          if (languageModel && typeof languageModel.get == "function") {
            var rootLanguageId = languageModel.get("rootLanguageId");
            return rootLanguageId == 1;
          }
          console.error(
            "Failed to determine whether language " + JSON.stringify(language) + " is English",
          );
          return false;
        },
        this,
      );
      Messenger.reqres.setHandler(
        "kb:standardResponsesType",
        function () {
          if (this.standardResponsesType) {
            return this.standardResponsesType;
          } else {
            this.standardResponsesType = new KbMenuModel.standardResponsesType({
              kbId: this.activeKbId,
            });
            return this.standardResponsesType;
          }
        },
        this,
      );
      //get all categories for this KB
      Messenger.reqres.setHandler(
        "kb:categories",
        function () {
          return this.categories;
        },
        this,
      );
      // Refetch campaigns against server
      Messenger.reqres.setHandler(
        "fetch:kb:campaigns",
        () => {
          this.campaigns.fetch({ reset: true });
          return this.campaigns;
        },
        this,
      );
      Messenger.reqres.setHandler(
        "fetch:kb:campaignsWithDefault",
        () => {
          this.campaignsWithDefault.fetch({ reset: true });
          return this.campaignsWithDefault;
        },
        this,
      );
      Messenger.reqres.setHandler(
        "kb:campaignsWithDefault",
        () => {
          return this.campaignsWithDefault;
        },
        this,
      );
      // Refetch categories against server
      Messenger.reqres.setHandler(
        "fetch:kb:categories",
        function () {
          this.categories.fetch({ reset: true });
          return this.categories;
        },
        this,
      );
      // get all attributes for this KB
      Messenger.reqres.setHandler(
        "kb:attributes",
        function () {
          return this.attributes;
        },
        this,
      );
      // get all attribute types
      Messenger.reqres.setHandler(
        "kb:attributeTypes",
        function () {
          return this.attributeTypes;
        },
        this,
      );

      //get all agents for this KB
      Messenger.reqres.setHandler(
        "kb:agents",
        function () {
          return this.agents;
        },
        this,
      );
      //get all mart (authoring) statuses for this KB
      Messenger.reqres.setHandler(
        "kb:mart:statuses",
        function () {
          return this.martStatuses;
        },
        this,
      );
      //get the roles for privileged areas
      Messenger.reqres.setHandler(
        "kb:privileged:area:roles",
        function () {
          return this.privilegedAreaRoles;
        },
        this,
      );
      //get the content awareness ignored urls
      Messenger.reqres.setHandler(
        "kb:content:awareness:ignored:urls",
        function () {
          return this.contentAwarenessIgnoredUrls;
        },
        this,
      );
      //get the current default URL from the server
      Messenger.reqres.setHandler(
        "kb:default:content:url",
        function () {
          return this.defaultUrl;
        },
        this,
      );
      //get whether the KB automagically converts backslashes to frontslashes
      Messenger.reqres.setHandler(
        "kb:convert:backslashes",
        function () {
          return this.convertBackslashes;
        },
        this,
      );
      //get a list of stopwords
      Messenger.reqres.setHandler(
        "kb:stop:words",
        function () {
          //return this.stopWords;
          //for now return a hard coded stop word set
          return [
            "a",
            "an",
            "the",
            "to",
            "in",
            "of",
            "if",
            "at",
            "or",
            "me",
            "my",
            "it",
            "we",
            "our",
            "the",
            "that",
            "these",
            "those",
            "my",
            "our",
            "your",
            "his",
            "her",
            "their",
            "this",
          ];
        },
        this,
      );
      //get the current KB Model
      Messenger.reqres.setHandler(
        "active:kb:model",
        function () {
          let kb;
          const kbId = this.activeKbId || Messenger.reqres.request("default:kbId");
          //goal here is to simulate fetching the kb model directly, while we actually obtain it from the kb collection
          if (this.kbs.isSynced) {
            kb = this.kbs.get(kbId);
            kb.isSynced = true; //mark as 'synced' as if we synced it from the server
            return kb;
          } else {
            //create a blank kb model to return while fetching
            kb = new KbMenuModel.kbModel({ kbId: kbId });
            kb.trigger("request"); //fake a request
            this.kbs.once(
              "sync",
              function () {
                kb.set(this.kbs.get(kbId).attributes, { silent: true }); //pass the attributes into the model we created earlier
                kb.trigger("fetched");
                kb.trigger("sync"); //trigger a sync event to simulate fetching this model from the server directly
              },
              this,
            );
            return kb;
          }
        },
        this,
      );
      Messenger.reqres.setHandler(
        "active:tenantId",
        function () {
          // If the active tenant has not been set, set it to the tenant of the user's default kb
          if (!this.activeTenantId) {
            var defaultKbId = Messenger.reqres.request("default:kbId");
            var kb = this.kbs.get(defaultKbId);
            this.activeTenantId = kb.get("tenantId");
            this.activeTenant = Messenger.reqres.request("fetch:tenant", this.activeTenantId);
          }
          return this.activeTenantId;
        },
        this,
      );
      Messenger.reqres.setHandler(
        "active:tenant",
        function () {
          // If the active tenant has not been set, set it to the tenant of the user's default kb
          if (!this.activeTenant) {
            var defaultKbId = Messenger.reqres.request("default:kbId");
            var kb = this.kbs.get(defaultKbId);
            this.activeTenantId = kb.get("tenantId");
            this.activeTenant = Messenger.reqres.request("fetch:tenant", this.activeTenantId);
          }
          return this.activeTenant;
        },
        this,
      );
    },
    _setUpCommands: function () {
      Messenger.command.setHandler(
        "set:active:kb",
        function (kbId) {
          console.log("set active KB");
          //make sure that the kbId is one that the user has permissions for
          var hasPermission = Messenger.reqres.request("can:view:kb", kbId);
          if (hasPermission) {
            this._setKbById(kbId);
            Messenger.command.execute("load:kb:decision:topics", kbId);
          } else {
            //if they cannot view the page, set the kbId to the default and send them to the dashboard
            var defaultKbId = Messenger.reqres.request("default:kbId");
            if (defaultKbId) {
              this._setKbById(defaultKbId);
              Messenger.vent.trigger("show:dashboard");
            }
          }
        },
        this,
      );
      Messenger.command.setHandler(
        "add:new:kb",
        function (kb) {
          this.kbs.add(kb);
        },
        this,
      );
      Messenger.command.setHandler(
        "set:active:tenant",
        function (tenant) {
          this.activeTenant = tenant;
          this.activeTenantId = tenant.get("tenantId");
        },
        this,
      );
    },
  });

  return KbMenuController;
});
